// =====================================================
// TABLE CELL-LEVEL DIFF — wrapped as ES module from src/js/docx-tables.js
// Logic unchanged.
// =====================================================

import { normalizeQuotes, tokenize, diffArrays, wordsEqual, refineWordReplacements, withWordIndices } from './diff-engine-core';
import { tagCrossRelIds } from './xml-helpers';
import { applyRevisionToAllRuns, preparePassthrough, diffRunsXml, appendedAnchors, addParagraphMarkRevision,
         getTrackChangesMode, getRecordOnly, getPreserveIncoming } from './docx-tracking';
import { addDeleteMarkup, addInsertMarkup } from './docx-markup';
import { recChange } from './change-record';
import { sideOf, layoutDiffers, allEqualDiff } from './pieces';
import { marksDiffer } from './inline';
import type { RichParagraph, DiffEntry, RichToken, CellInfo, InlineEvent } from '../types';

/** How a changed cell's paragraphs line up: the same paragraphs (by text) in order; between them,
 *  paragraphs paired in order as edited, and the rest wholly deleted or inserted. */
export type CellItem =
  | { kind: 'equal' | 'modify'; orig: RichParagraph; rev: RichParagraph }
  | { kind: 'delete'; orig: RichParagraph }
  | { kind: 'insert'; rev: RichParagraph };

/**
 * The paragraph plan for a changed cell, shared by the writer, the renderer and the change list,
 * so each paragraph keeps its own properties and Accept/Reject All keep the cell's paragraphs.
 * Null when the cell must be compared as one paragraph: a paragraph without XML (text or PDF
 * input) or a nested table.
 */
export function cellParagraphPlan(oc: CellInfo, rc: CellInfo): CellItem[] | null {
  if (!oc.paragraphs.length || !rc.paragraphs.length) return null;
  if (/<w:tbl\b/.test(oc.rawXml || '') || /<w:tbl\b/.test(rc.rawXml || '')) return null;
  if ([...oc.paragraphs, ...rc.paragraphs].some(p => !p.rawXml)) return null;
  const key = (p: RichParagraph) => normalizeQuotes((p.text || '').replace(/\s+/g, ' ').trim());
  const items: CellItem[] = [];
  let dels: RichParagraph[] = [], inss: RichParagraph[] = [];
  const flush = () => {
    const n = Math.min(dels.length, inss.length);
    for (let i = 0; i < n; i++) items.push({ kind: key(dels[i]) === key(inss[i]) ? 'equal' : 'modify', orig: dels[i], rev: inss[i] });
    for (const o of dels.slice(n)) items.push({ kind: 'delete', orig: o });
    for (const r of inss.slice(n)) items.push({ kind: 'insert', rev: r });
    dels = []; inss = [];
  };
  for (const d of diffArrays(oc.paragraphs.map(key), rc.paragraphs.map(key))) {
    if (d.type === 'equal') { flush(); items.push({ kind: 'equal', orig: oc.paragraphs[d.aIdx!], rev: rc.paragraphs[d.bIdx!] }); }
    else if (d.type === 'delete') dels.push(oc.paragraphs[d.idx!]);
    else inss.push(rc.paragraphs[d.idx!]);
  }
  flush();
  return items;
}

/** The writer's word diff of a cell (or of one cell paragraph), with word indices filled in and
 *  the same refinement as the body (an edit inside a small one-word swap, punctuation kept beside
 *  a changed word), so a cell reads the same in the .docx, the pages and the app. The summary and
 *  the change list count a cell's changes from this diff too. */
export function cellWordDiff(origText: string, revText: string): DiffEntry[] {
  return refineWordReplacements(withWordIndices(diffArrays(tokenize(origText), tokenize(revText), wordsEqual)));
}

/** A changed row can be compared cell by cell: both sides have cells, and no cell holds blocks
 *  other than paragraphs (CellData.opaque). Otherwise the writer shows the old row deleted and the
 *  new one inserted, and the summary, change list and pages count it that way. */
export function rowCellsComparable(p: { origRich?: RichParagraph | null; revRich?: RichParagraph | null }): boolean {
  const o = p.origRich?.cellData, r = p.revRich?.cellData;
  return !!(o && r && !o.opaque && !r.opaque);
}

/** An unchanged row whose cells differ in a tab or line break (pieces.ts layoutDiffers): written
 *  as an edited row in tracked output, so the change is tracked. */
export function rowLayoutDiffers(o: RichParagraph, r: RichParagraph): boolean {
  const oc = o.cellData?.cells, rc = r.cellData?.cells;
  if (!oc || !rc || oc.length !== rc.length || o.cellData!.opaque || r.cellData!.opaque) return false;
  return oc.some((c, k) => cellLayoutDiffers(c, rc[k]));
}
/** An unchanged row whose cell paragraphs differ in a footnote mark, a picture, a field or a symbol
 *  (inline.ts marksDiffer): written as an edited row, so the mark is tracked. */
export function rowMarksDiffer(o: RichParagraph, r: RichParagraph): boolean {
  const oc = o.cellData?.cells, rc = r.cellData?.cells;
  if (!oc || !rc || oc.length !== rc.length || o.cellData!.opaque || r.cellData!.opaque) return false;
  return oc.some((c, k) => cellMarksDiffer(c, rc[k]));
}
const cellMarksDiffer = (oc: CellInfo, rc: CellInfo): boolean =>
  oc.paragraphs.length === rc.paragraphs.length && oc.paragraphs.some((p, j) => marksDiffer(p, rc.paragraphs[j]));
function cellLayoutDiffers(oc: CellInfo, rc: CellInfo): boolean {
  if (oc.paragraphs.length && oc.paragraphs.length === rc.paragraphs.length)
    return oc.paragraphs.some((p, k) => layoutDiffers(sideOf(p), sideOf(rc.paragraphs[k])));
  return layoutDiffers(sideOf(oc), sideOf(rc));
}

export function buildRedlineTableRow(origRich: RichParagraph, revRich: RichParagraph): string {
  const origCells = origRich.cellData!.cells;
  const revCells = revRich.cellData!.cells;
  const maxCells = Math.max(origCells.length, revCells.length);

  let xml = `<w:tr>${revRich.cellData!.trPrXml}`;

  for (let c = 0; c < maxCells; c++) {
    const origCell = c < origCells.length ? origCells[c] : null;
    const revCell = c < revCells.length ? revCells[c] : null;

    // (each change is recorded as it is written, keyed by its cell: change-record.ts)
    if (origCell && revCell) {
      xml += buildRedlineCell(origCell, revCell, c);
    } else if (origCell && !revCell) {
      recChange(`c${c}.w`, { type: 'delete', scope: 'words', cell: c });
      if (!xmlSkip()) xml += applyRevisionToAllRuns(tagCrossRelIds(origCell.rawXml), addDeleteMarkup, 'delete');
    } else if (!origCell && revCell) {
      recChange(`c${c}.w`, { type: 'insert', scope: 'words', cell: c });
      if (!xmlSkip()) xml += applyRevisionToAllRuns(revCell.rawXml, addInsertMarkup, 'insert');
    }
  }

  xml += '</w:tr>';
  return xml;
}

/** The writer run for its record alone (recordChanges), in markup: no revision ids to give, so a
 *  cell's whole and unchanged parts are recorded and not written. */
const xmlSkip = () => getRecordOnly() && !getTrackChangesMode();

function buildRedlineCell(origCell: CellInfo, revCell: CellInfo, c: number): string {
  // (the same words: as the revised draft has it, unless a cell paragraph's footnote marks, pictures,
  // fields or symbols differ: then paragraph by paragraph, so the mark is tracked)
  if (normalizeQuotes(origCell.text.trim()) === normalizeQuotes(revCell.text.trim())
      && !(!getPreserveIncoming() && cellMarksDiffer(origCell, revCell))) {
    return getRecordOnly() ? '' : preparePassthrough(revCell.rawXml);
  }
  const plan = cellParagraphPlan(origCell, revCell);
  if (!plan) {
    // compared as one paragraph (see cellParagraphPlan)
    const runs = diffRuns(origCell, revCell, `c${c}.`, c);
    if (runs == null) return preparePassthrough(revCell.rawXml);
    const pPrXml = (revCell.paragraphs.length > 0) ? revCell.paragraphs[0].pPrXml : '';
    return `<w:tc>${revCell.tcPrXml}<w:p>${pPrXml}${runs}</w:p></w:tc>`;
  }
  // paragraph by paragraph: each keeps its own properties; a wholly deleted or inserted
  // paragraph revises its mark too (track mode). The cell's last mark holds the cell end and
  // cannot be revised: when the cell ends with wholly deleted (or inserted) paragraphs, the mark
  // before them is revised instead, as Word does, so the paragraph before runs on into them.
  const track = getTrackChangesMode();
  const lastKind = plan[plan.length - 1].kind;
  let tail = plan.length;   // first index of the cell's trailing run of whole deletions/insertions
  if (lastKind === 'delete' || lastKind === 'insert') while (tail > 0 && plan[tail - 1].kind === lastKind) tail--;
  // A wholly deleted or inserted paragraph is one change, its mark included; the mark revised for
  // the cell's trailing ones goes with the last of them.
  const whole = (k: number) => recChange(`c${c}.p${k}`, { type: plan[k].kind === 'delete' ? 'delete' : 'insert', scope: 'words', cell: c });
  const paras = plan.map((it, k) => {
    const last = k === plan.length - 1;
    const markAs: 'inserted' | 'deleted' | null =
      it.kind === 'delete' ? 'deleted' : it.kind === 'insert' ? 'inserted'
        : (k === tail - 1 ? (lastKind === 'delete' ? 'deleted' : 'inserted') : null);
    const mark = (xml: string) => {
      if (!(track && !last && markAs)) return xml;
      whole(it.kind === 'delete' || it.kind === 'insert' ? k : plan.length - 1);
      return addParagraphMarkRevision(xml, markAs);
    };
    if (it.kind === 'equal') {
      // (a tab or line break that differs: written as an edited paragraph, tracked; not in Combine,
      // which keeps the revised draft's unchanged paragraphs as they are)
      // (a footnote mark, picture, field or symbol only one draft has there: the same, in both modes)
      if (!getPreserveIncoming() && ((track && layoutDiffers(sideOf(it.orig), sideOf(it.rev))) || marksDiffer(it.orig, it.rev))) {
        const runs = diffRuns(it.orig, it.rev, `c${c}.p${k}.`, c, allEqualDiff(sideOf(it.orig), sideOf(it.rev)));
        if (runs != null) return mark(getRecordOnly() ? '<w:p/>' : `<w:p>${it.rev.pPrXml}${runs}</w:p>`);
      }
      return mark(getRecordOnly() ? '<w:p/>' : preparePassthrough(it.rev.rawXml!));
    }
    if (it.kind === 'delete') { whole(k); return xmlSkip() ? '' : mark(applyRevisionToAllRuns(tagCrossRelIds(it.orig.rawXml!), addDeleteMarkup, 'delete')); }
    if (it.kind === 'insert') { whole(k); return xmlSkip() ? '' : mark(applyRevisionToAllRuns(it.rev.rawXml!, addInsertMarkup, 'insert')); }
    const runs = diffRuns(it.orig, it.rev, `c${c}.p${k}.`, c);
    return mark(runs == null ? preparePassthrough(it.rev.rawXml!) : `<w:p>${it.rev.pPrXml}${runs}</w:p>`);
  });
  return `<w:tc>${revCell.tcPrXml}${paras.join('')}</w:tc>`;
}

/** Word-diff runs for one cell or cell paragraph (text, rich tokens and XML of each side), with the
 *  anchors the rebuild would otherwise drop; null when nothing meaningful changed. */
function diffRuns(orig: { text: string; richTokens?: RichToken[]; rawXml: string | null; inline?: InlineEvent[] }, rev: { text: string; richTokens?: RichToken[]; rawXml: string | null; inline?: InlineEvent[] }, keyPrefix: string, cell: number, given?: DiffEntry[] | null): string | null {
  const filteredDiff = given || cellWordDiff(orig.text, rev.text).filter(d => d.value.trim() || d.type === 'equal');
  // (no word changed: only a tab or line break that differs, tracked output)
  if (!filteredDiff.some(d => d.type !== 'equal') && !(!getPreserveIncoming() && ((getTrackChangesMode() && layoutDiffers(sideOf(orig), sideOf(rev))) || marksDiffer(orig, rev)))) return null;

  // the same writer as body paragraphs: the source's spacing, one revision per run of changed
  // words, tracked format changes on unchanged words, and footnote marks, comments, pictures,
  // bookmarks, links and fields where they were (a cell paragraph's are recorded by the parser)
  const runs = diffRunsXml(filteredDiff, orig, rev, { keyPrefix, cell, spacing: true });
  if (orig.inline || rev.inline) return runs;
  // a cell compared as one paragraph: what the token rebuild would otherwise drop goes at the end
  // (without it, comments in the cell vanish, orphaning comments.xml, and footnote marks are lost)
  return runs + appendedAnchors(orig, rev);
}
